import React, { useState, useMemo } from "react";
import { Layout } from "Components/Layout/Layout";
import { Footer } from "Components/Footer/Footer";
import { CourseCard } from "Components/CourseCard/CourseCard";
import { Pagination } from "Components/Pagination/Pagination";
import { useMobile } from "Hooks/useMobile";
import { InformativeText } from "Components/InformativeText/InformativeText";
import { NotebookIcon } from "Components/Icons/Documents/NotebookIcon";
import { CourseCardSkeleton } from "Components/CourseCardSkeleton/CourseCardSkeleton";
import { PAGINATION, HTTP_METHODS, HTTP_HEADERS } from "Constantes/constants";
import { useQuery } from "Hooks/useQuery";
import { getMisCapacitacionesPresenciales } from "Services/mis_capacitaciones_presenciales_service";
import { storageService } from "Services/storage_service";
import { paramService } from "Services/param_service";
import { saveAs } from "file-saver";
import "./CapacitacionesPresenciales.scss";

export const CapacitacionesPresenciales = () => {
  const isMobile = useMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloadingDiploma, setIsDownloadingDiploma] = useState({});
  const itemsPerPage = isMobile
    ? PAGINATION.ITEMS_PER_PAGE_MOBILE
    : PAGINATION.ITEMS_PER_PAGE_DESKTOP;

  const user = storageService.getItem("user");
  const dpi = user?.usuario;

  const { data: capacitacionesData, isLoading } = useQuery(
    getMisCapacitacionesPresenciales,
    dpi,
    Boolean(dpi)
  );

  const { data: apiDiploma } = useQuery(paramService.param, {
    grupo: "apis",
  });

  const getParamValue = (data, codigo) => {
    if (!data || !Array.isArray(data)) return null;
    const param = data.find((p) => p.codigoParametro === codigo);
    return param?.parametro || null;
  };

  const endpointDiplomaPresencial = getParamValue(apiDiploma?.data, "apiDiplomaPresencial") || null;

  const capacitaciones = useMemo(
    () => capacitacionesData || [],
    [capacitacionesData]
  );

  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const slicedData = capacitaciones.slice(startIndex, endIndex);
    const total = Math.ceil(capacitaciones.length / itemsPerPage);

    return {
      paginatedData: slicedData,
      totalPages: total,
    };
  }, [capacitaciones, currentPage, itemsPerPage]);

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const onDownloadDiploma = async (capacitacion) => {
    if (!endpointDiplomaPresencial || !user) return;

    const participanteId = capacitacion.idParticipante;
    setIsDownloadingDiploma(prev => ({ ...prev, [participanteId]: true }));

    try {
      const diplomaData = {
        idParticipante: capacitacion.idParticipante,
      };

      const response = await fetch(endpointDiplomaPresencial, {
        method: HTTP_METHODS.POST,
        headers: {
          "Content-Type": HTTP_HEADERS.CONTENT_TYPE_JSON,
        },
        body: JSON.stringify(diplomaData),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const fileName = `DIPLOMA ${capacitacion.nombreCurso}.pdf`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error descargando diploma:", error);
    } finally {
      setIsDownloadingDiploma(prev => ({ ...prev, [participanteId]: false }));
    }
  };

  return (
    <Layout>
      <div className="capacitaciones-presenciales">
        <div className="capacitaciones-presenciales__header">
          <h1 className="capacitaciones-presenciales__title">
            Capacitaciones Presenciales
          </h1>
        </div>

        <div className="capacitaciones-presenciales__container">
          {!isLoading && capacitaciones.length === 0 && (
            <div className="capacitaciones-presenciales__empty-state">
              <InformativeText>
                <NotebookIcon />
                Aún no tienes capacitaciones presenciales asignadas.
              </InformativeText>
            </div>
          )}

          <div className="capacitaciones-presenciales__grid">
            {!isLoading &&
              paginatedData.map((capacitacion) => (
                <CourseCard
                  key={capacitacion.idParticipante}
                  title={capacitacion.nombreCurso}
                  imageUrl={capacitacion.img}
                  subtitle={capacitacion.institucion}
                  variant="capacitacion-presencial"
                  isLoggedIn={true}
                  onDownloadDiploma={() => onDownloadDiploma(capacitacion)}
                  isDownloadingDiploma={isDownloadingDiploma[capacitacion.idParticipante]}
                />
              ))}

            {isLoading &&
              Array.from({ length: itemsPerPage }).map((_, index) => (
                <CourseCardSkeleton
                  key={index}
                  variant="capacitacion-presencial"
                />
              ))}
          </div>
        </div>

        {capacitaciones.length > 0 && (
          <div className="capacitaciones-presenciales__pagination-container">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>
      <Footer />
    </Layout>
  );
};
